// =============================================================================
// bootstrapApp() — one-shot bootstrap thunk dispatched once from App.js mount.
//
// Lifecycle (state.bootstrap.phase + state.profile.hydration):
//
//   No auth in legacy userInfo:
//     phase: 'hydrating' (preloadedState) → BOOTSTRAP_READY → 'ready'
//     hydration: 'unauthenticated' (preloadedState) → unchanged
//
//   Auth present, profile fetch SUCCESS:
//     phase: 'hydrating' (preloadedState) → BOOTSTRAP_READY → 'ready'
//     hydration: 'pending' (preloadedState) → SUCCESS → 'success'
//
//   Auth present, profile fetch FAILED (network / 5xx / timeout):
//     phase: 'hydrating' (preloadedState) → BOOTSTRAP_READY → 'ready'
//     hydration: 'pending' (preloadedState) → FAILED → 'failed'
//     (profile.data keeps last-known stale value from preloadedState seed)
//
// Idempotency:
//   - Module-scope inFlight ref coalesces double-dispatch (React StrictMode).
//   - Post-completion: state.bootstrap.phase === 'ready' short-circuits.
//   - BOOTSTRAP_READY itself is idempotent at reducer level.
//
// Auth/profile mismatch windows (becomes critical in Phase 4 dual-write):
// -----------------------------------------------------------------------------
// During hydration there exists a window where state.auth (preloaded from
// legacy blob) and state.profile.data (also preloaded) reflect *different
// freshness levels* than each other and than the canonical state.userLogin.
// userInfo blob. Specifically:
//
//   T0 — preloadedState seeds: auth = legacy snapshot, profile.data = legacy
//        snapshot (same blob source — consistent at T0).
//   T1 — bootstrapApp dispatches PROFILE_HYDRATION_PENDING. profile.data
//        remains the T0 stale snapshot (do NOT clear — see "stale cache"
//        contract in profileReducers.js).
//   T2 — profile fetch resolves. profile.data flips to fresh server data.
//        state.userLogin.userInfo is NOT updated (Phase 2 does not dual-write).
//        state.auth.{userId,publicId,email,isEmailVerified} ALSO not updated.
//        So profile.data is now FRESH while auth identity fields are STALE-
//        from-localStorage. They likely agree (same user), but consumer that
//        joins both via separate selectors must tolerate momentary drift.
//
// Phase 4 will introduce dual-write — at that point the same login/refresh
// payload populates BOTH auth and profile.data within a single tick, and the
// drift window collapses. Phase 5 consumer migration MUST land AFTER Phase 4
// for this reason.
// -----------------------------------------------------------------------------
//
// Phase 2 reads auth from LEGACY state.userLogin.userInfo on purpose — auth
// ownership transfer is a Phase 4/6 concern. Phase 2 is hydration only.
// =============================================================================

import api from '../api/axios';
import {
  PROFILE_HYDRATION_PENDING,
  PROFILE_HYDRATION_SUCCESS,
  PROFILE_HYDRATION_FAILED,
  PROFILE_HYDRATION_UNAUTHENTICATED,
} from '../constants/profileConstants';
import { BOOTSTRAP_READY } from '../constants/bootstrapConstants';
import { AUTH_IDENTITY_PATCH } from '../constants/authConstants';
import { selectAccessToken } from '../selectors/userSelectors';

// Hard ceiling on profile fetch. axios timeout throws ECONNABORTED, caught by
// the surrounding try/catch and routed to PROFILE_HYDRATION_FAILED. The
// finally block then dispatches BOOTSTRAP_READY, lifting the Splash gate.
// No retry — a single failure attempt becomes the bootstrap result; user
// lands in the app with stale-cache profile and may pull-to-refresh later.
const PROFILE_FETCH_TIMEOUT_MS = 8000;

let inFlight = null;

export const bootstrapApp = () => async (dispatch, getState) => {
  if (getState().bootstrap?.phase === 'ready') return;
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      // R5.5: access read routed through the compatibility selector. Internal
      // projection is byte-identical to the legacy read today (selectAccessToken
      // reads state.userLogin.userInfo.access || null); Phase 6 will swap the
      // selector's internal source to state.auth.access transparently.
      console.log("[bootstrap] start");
      const access = selectAccessToken(getState());

      if (!access) {
        dispatch({ type: PROFILE_HYDRATION_UNAUTHENTICATED });
        console.log("[bootstrap] unauthenticated");
        return;
      }
      console.log("[bootstrap] access present");

      dispatch({ type: PROFILE_HYDRATION_PENDING });

      try {
        const { data } = await api.get('/users/profile/', {
          timeout: PROFILE_FETCH_TIMEOUT_MS,
        });
        console.log("[bootstrap] hydration success");
        dispatch({ type: PROFILE_HYDRATION_SUCCESS, payload: data });

        // Phase 4B — eventually-consistent identity reconciliation into
        // auth slice. Picks up server-side identity changes that happened
        // since the last persist (e.g. is_email_verified flipped via Google
        // login in another session, admin-side verification). PATCH-semantic
        // — tokens are NOT in the /users/profile/ response and stay
        // untouched. See authReducers.js AUTH_IDENTITY_PATCH for the full
        // contract (whitelist + eventually-consistent caveat).
        dispatch({ type: AUTH_IDENTITY_PATCH, payload: data });
      } catch (err) {
        // All failure modes route here:
        //   - network error / DNS / offline
        //   - 5xx
        //   - timeout (axios throws ECONNABORTED after PROFILE_FETCH_TIMEOUT_MS)
        //   - 401 → axios interceptor → R0 coordinator refresh; if refresh
        //     fails the coordinator's performLogout navigates away and this
        //     dispatch lands on a discarded JS context (harmless).
        // Single-shot: no retry storm. User lands with stale-cache profile.
        console.log("[bootstrap] hydration failed");
        dispatch({
          type: PROFILE_HYDRATION_FAILED,
          payload:
            err?.response?.data?.detail || err?.message || 'Profile fetch failed',
        });
      }
    } finally {
      // CRITICAL ORDER — release in-flight lock BEFORE dispatching the
      // completion signal. Even if BOOTSTRAP_READY dispatch were to throw
      // (it won't — Redux dispatch on a configureStore-built store is pure),
      // the lock cannot strand. State-level guard at the top of the thunk
      // prevents re-entry from picking up where a hypothetically-failed
      // BOOTSTRAP_READY left off.
      inFlight = null;
      console.log("[bootstrap] ready");
      dispatch({ type: BOOTSTRAP_READY });
    }
  })();

  return inFlight;
};

// Test/dev hook — clear in-flight ref between specs.
export function __resetBootstrap() {
  inFlight = null;
}
